import { ChevronRight } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  useSidebar,
} from "~/components/ui";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "~/components/ui";
import { cn, findIcon } from "~/lib/utils";

import { ModuleGroup } from ".";

interface NavMainProps {
  group: ModuleGroup;
}
export function NavMain({ group }: NavMainProps) {
  const { state } = useSidebar();
  const location = useLocation();

  const isActiveRoute = (path: string) => location.pathname.includes(path);

  return (
    <SidebarGroup
      className={cn("-ml-2", {
        "mt-auto": group.heading === "Extras",
      })}
    >
      <SidebarGroupLabel className="pb-3 pl-7 text-[10px] uppercase tracking-wide text-accent-500">
        {group.heading === "Extras" || group.heading === "General"
          ? ""
          : group.heading}
      </SidebarGroupLabel>

      <SidebarMenu>
        {group.menus.map((item) => {
          const finditem = findIcon(item.icon as string, item.name);
          const findPath = finditem?.path ?? item.route ?? "";

          const isActive = isActiveRoute(findPath);

          if (item.children.length > 0) {
            const parentNavItem = findIcon(item.icon as string, item.name);
            const isAnyChildActive = item.children?.some((subItem) => {
              const matchedSubNavItem = parentNavItem?.subItems?.find(
                (sub) =>
                  sub.title.toLowerCase().trim() ===
                    subItem.name.toLowerCase().trim() ||
                  sub.path
                    .toLowerCase()
                    .includes(subItem.name.toLowerCase().trim()) ||
                  (subItem.route &&
                    sub.path.toLowerCase() === subItem.route.toLowerCase()),
              );
              const findSubPath = (matchedSubNavItem?.path ||
                subItem.route ||
                (subItem as any).Route) as string;
              return findSubPath
                ? location.pathname.startsWith(findSubPath)
                : false;
            });
            const isParentActive = isActive || isAnyChildActive;

            return (
              <Collapsible
                key={item.name}
                asChild
                defaultOpen={isParentActive}
                className="group/collapsible"
              >
                <SidebarMenuItem className="group flex w-full flex-col items-center">
                  <div className="flex w-full items-center">
                    {state === "expanded" && (
                      <div
                        className={cn(
                          "ml-0 mr-4 h-[40px] w-[5px] rounded-r-md transition-colors",
                          isParentActive
                            ? "bg-secondary-500"
                            : "bg-primary-500",
                        )}
                      />
                    )}
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton
                        tooltip={item.name}
                        className={cn("rounded-[20px]", {
                          "w-11/12": state === "expanded",
                          "w-full": state === "collapsed",
                        })}
                      >
                        <span className="flex w-full items-center py-5">
                          {findIcon(item.icon as string, item.name)?.navIcon({
                            size: 20,
                            className: cn({
                              "text-primary-500 ml-2": isActive,
                              "text-secondary-500 ml-2":
                                (!isActive && state === "expanded") ||
                                (!isActive && state === "collapsed"),
                            }),
                          })}
                          <div
                            className={cn("px-4 font-Regular text-sm", {
                              "font-Bold": isParentActive,
                            })}
                          >
                            {item.name}
                          </div>
                          <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]:rotate-90" />
                        </span>
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                  </div>
                  <CollapsibleContent className="w-full">
                    <SidebarMenuSub>
                      {item.children?.map((subItem) => {
                        const matchedSubNavItem = parentNavItem?.subItems?.find(
                          (sub) =>
                            sub.title.toLowerCase().trim() ===
                              subItem.name.toLowerCase().trim() ||
                            sub.path
                              .toLowerCase()
                              .includes(subItem.name.toLowerCase().trim()) ||
                            (subItem.route &&
                              sub.path.toLowerCase() ===
                                subItem.route.toLowerCase()),
                        );
                        const findSubPath = (matchedSubNavItem?.path ||
                          subItem.route ||
                          (subItem as any).Route) as string;
                        const isSubtActive = findSubPath
                          ? location.pathname.startsWith(findSubPath)
                          : false;
                        return (
                          <SidebarMenuSubItem key={subItem.name}>
                            <SidebarMenuSubButton
                              className={`my-1 rounded-[20px] pl-14 pr-4 ${isSubtActive ? "hover:bg-secondary-500 hover:text-black" : null}`}
                              isActive={isSubtActive}
                              asChild
                            >
                              <NavLink to={findSubPath} className="py-5">
                                <span
                                  className={cn("font-Regular text-sm", {
                                    "font-Bold": isSubtActive,
                                  })}
                                >
                                  {subItem.name}
                                </span>
                              </NavLink>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        );
                      })}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            );
          } else {
            return (
              <SidebarMenuItem
                key={item.name}
                className="flex w-full items-center"
              >
                {isActive && state === "expanded" ? (
                  <div className="ml-0 mr-4 h-[40px] w-[5px] rounded-r-md bg-secondary-500"></div>
                ) : !isActive && state === "expanded" ? (
                  <div className="ml-0 mr-4 h-[40px] w-[5px] rounded-r-md bg-primary-500"></div>
                ) : null}
                <SidebarMenuButton
                  asChild
                  isActive={isActive}
                  className={cn(
                    `w-full rounded-[20px] ${isActive ? "hover:bg-secondary-500 hover:text-black" : null}`,
                    {
                      "w-11/12": state === "expanded",
                    },
                  )}
                >
                  <NavLink
                    to={
                      (findIcon(item.icon as string, item.name)
                        ?.path as string) || (item.route as string)
                    }
                    className="py-5"
                  >
                    {findIcon(item.icon as string, item.name)?.navIcon({
                      size: 20,
                      className: cn({
                        "text-primary-500 ml-2": isActive,
                        "text-secondary-500 ml-2":
                          (!isActive && state === "expanded") ||
                          (!isActive && state === "collapsed"),
                      }),
                    })}
                    <div
                      className={cn("px-4 font-Regular text-sm", {
                        "font-Bold": isActive,
                      })}
                    >
                      {item.name}
                    </div>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          }
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}
